import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AnimatedPageLoader } from 'smartsuite-ui';
import InitialState from './stateManagement/InitialState';
import DashboardContext from '../shared/hooks/dashboardContext';
import widgetDrawConfigurations from './widgets/widgetDrawConfigurations';
import ExternalState from './stateManagement/ExternalState';
import Dashboard from './Dashboard';
import ViewController from './stateManagement/ViewState';
import { getDashboardContextValue } from './dashboardUtils';

export default function DashboardLayout(props) {
    const widgetRenderers = useMemo(
        () => ({
            ...widgetDrawConfigurations,
            ...props.customizations.widgetRenderers,
        }),
        [props.customizations.widgetRenderers]
    );

    function renderDashboard(
        changeView,
        deleteSavedDashboard,
        isLoaded,
        loadedSavedDashboard,
        loadSavedDashboard,
        saveDashboard,
        savedDashboardError,
        savedDashboardNames,
        viewOptions
    ) {
        if (!isLoaded) {
            return <AnimatedPageLoader />;
        }

        return (
            <Dashboard
                changeView={changeView}
                dashboardKey={props.dashboardKey}
                deleteSavedDashboard={deleteSavedDashboard}
                loadSavedDashboard={loadSavedDashboard}
                saveDashboard={saveDashboard}
                savedDashboardError={savedDashboardError}
                savedDashboardNames={savedDashboardNames}
                viewConfiguration={loadedSavedDashboard}
                viewOptions={viewOptions}
            />
        );
    }

    return (
        <InitialState
            dashboardKey={props.dashboardKey}
            favoriteUtilities={props.favoriteUtilities}
            uriConstants={props.uriConstants}
            widgetRenderers={widgetRenderers}
            render={({
                initialStateLoaded,
                selectOptions,
                thresholdValues,
                views,
                systemManifest,
            }) => (
                <ViewController
                    viewKey={props.viewKey}
                    views={views}
                    render={({ activeView, changeView, viewOptions }) => (
                        <ExternalState
                            dashboardKey={props.dashboardKey}
                            initialConfiguration={activeView}
                            initialStateLoaded={initialStateLoaded}
                            location={props.location}
                            selectOptions={selectOptions}
                            uriConstants={props.uriConstants}
                            render={({
                                deleteSavedDashboard,
                                isLoaded,
                                loadedSavedDashboard,
                                loadSavedDashboard,
                                savedDashboardError,
                                savedDashboardNames,
                                saveDashboard,
                            }) => (
                                <DashboardContext.Provider
                                    value={getDashboardContextValue(
                                        props,
                                        selectOptions,
                                        thresholdValues,
                                        widgetRenderers,
                                        systemManifest
                                    )}
                                >
                                    {renderDashboard(
                                        changeView,
                                        deleteSavedDashboard,
                                        isLoaded,
                                        loadedSavedDashboard,
                                        loadSavedDashboard,
                                        saveDashboard,
                                        savedDashboardError,
                                        savedDashboardNames,
                                        viewOptions
                                    )}
                                </DashboardContext.Provider>
                            )}
                        />
                    )}
                />
            )}
        />
    );
}

DashboardLayout.propTypes = {
    customizations: PropTypes.instanceOf(Object).isRequired,
    dashboardKey: PropTypes.string.isRequired,
    favoriteUtilities: PropTypes.instanceOf(Object).isRequired,
    location: PropTypes.instanceOf(Object),
    uriConstants: PropTypes.instanceOf(Object).isRequired,
    viewKey: PropTypes.string,
};

DashboardLayout.defaultProps = {
    location: {},
    viewKey: '',
};
